import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, NavLink, useNavigate } from 'react-router-dom';
import { useEmployeeAttendanceAuth } from '../context/EmployeeAttendanceAuthContext';
import { useAttendanceAuth } from '../../attendance/context/AttendanceAuthContext';
import attendanceAPI from '../../attendance/api/attendanceAPI';
import {
  CAMERA_PENDING_CHANGED_EVENT,
  hasCameraFacultyRole,
  hasCameraMediaRole,
} from '../../attendance/utils/cameraBookingUtils';
import { getInitials } from '../utils/attendanceFormatters';
import { STAFF_ATTENDANCE_UI_ENABLED } from '../utils/roleAccess';
import {
  MdLogout,
  MdMenu,
  MdClose,
  MdPerson,
  MdLock,
  MdExpandMore,
  MdApps,
} from 'react-icons/md';
import '../employeeAttendance.css';
import '../employeeAttendance-compact.css';
import '../employeeAttendance-history-dashboard.css';
import '../employeeAttendance-hr.css';
import '../employeeAttendance-admissions.css';

const BU_LOGO =
  'https://bu-page-assets.s3.ap-northeast-1.amazonaws.com/static-assets/others/Benadir University Logo.png';

const APPS = {
  tutor: {
    key: 'tutor',
    name: 'Tutor',
    home: '/attendance/tutor/dashboard',
    menus: [
      { to: '/attendance/tutor/dashboard', label: 'Dashboard' },
      { to: '/attendance/tutor/today', label: "Today's Attendance" },
      { to: '/attendance/tutor/calendar', label: 'Weekly Calendar' },
      { to: '/attendance/tutor/history', label: 'History' },
      { to: '/attendance/tutor/reports', label: 'Attendance Reports' },
    ],
  },
  facultyAdmin: {
    key: 'facultyAdmin',
    name: 'Faculty Admin',
    home: '/attendance/faculty-admin/dashboard',
    menus: [
      { to: '/attendance/faculty-admin/dashboard', label: 'Dashboard' },
      { to: '/attendance/faculty-admin/timetables', label: 'Timetables' },
      { to: '/attendance/faculty-admin/sessions', label: 'Attendance' },
      { to: '/attendance/faculty-admin/teacher-sheet-report', label: 'Teacher Sheet Report' },
      { to: '/attendance/faculty-admin/search-student-marks', label: 'Search Student Marks' },
      {
        label: 'Attendance Reports',
        children: [
          { to: '/attendance/faculty-admin/attendance-stats', label: 'Batch Report' },
          { to: '/attendance/faculty-admin/subject-report', label: 'Subject Report' },
        ],
      },
    ],
  },
  staff: {
    key: 'staff',
    name: 'Attendance',
    home: '/bu-dashboard/dashboard',
    menus: [
      { to: '/bu-dashboard/dashboard', label: 'Today' },
      { to: '/bu-dashboard/history', label: 'History' },
    ],
  },
  hr: {
    key: 'hr',
    name: 'HR',
    home: '/bu-dashboard/hr/employees',
    menus: [
      { to: '/bu-dashboard/hr/employees', label: 'Employees' },
      { to: '/bu-dashboard/hr/attendance', label: 'Attendances' },
      { to: '/bu-dashboard/hr/reports', label: 'Reporting' },
      { to: '/bu-dashboard/hr/portal-access', label: 'Portal Access' },
      { to: '/bu-dashboard/hr/hikvision/punches', label: 'Hikvision' },
    ],
  },
  admission: {
    key: 'admission',
    name: 'Admissions',
    home: '/bu-dashboard/admissions/students',
    menus: [
      { to: '/bu-dashboard/admissions/students', label: 'Students Admission' },
      { to: '/bu-dashboard/admissions/prospective', label: 'Prospective Students' },
      { to: '/bu-dashboard/admissions/ug-student-list', label: 'UG Student List Report' },
    ],
  },
  postgraduate: {
    key: 'postgraduate',
    name: 'Postgraduate',
    home: '/bu-dashboard/postgraduate/students',
    menus: [
      { to: '/bu-dashboard/postgraduate/students', label: 'Postgraduate Student Admissions' },
      { to: '/bu-dashboard/postgraduate/student-list', label: 'PG Students List Report' },
      { to: '/bu-dashboard/postgraduate/teacher-sheet', label: 'PG Teacher Sheet' },
    ],
  },
  exam: {
    key: 'exam',
    name: 'Exams',
    home: '/bu-dashboard/exams/entries',
    menus: [
      { to: '/bu-dashboard/exams/entries', label: 'Multi Exam Entry' },
      {
        label: 'Reports',
        children: [
          {
            to: '/bu-dashboard/exams/teacher-sheets',
            label: 'Teacher Sheet Marks',
          },
          {
            to: '/bu-dashboard/exams/search-student-marks',
            label: 'Search Student Mark',
          },
        ],
      },
    ],
  },
};

/**
 * Utilities menu. Requests are only for child companies and deposits only for
 * the parent, so each link appears when at least one company can use it.
 */
function buildUtilitiesApp(companies = []) {
  const hasChild = companies.length === 0 || companies.some((c) => !c.is_parent);
  const hasParent = companies.length === 0 || companies.some((c) => c.is_parent);
  const menus = [
    { to: '/bu-dashboard/utilities/dashboard', label: 'Dashboard' },
    { to: '/bu-dashboard/utilities/vendors', label: 'Vendors' },
    { to: '/bu-dashboard/utilities/bills', label: 'Bills' },
    { to: '/bu-dashboard/utilities/single-payments', label: 'Single Payments' },
    { to: '/bu-dashboard/utilities/batch-payments', label: 'Batch Payments' },
  ];
  if (hasChild) menus.push({ to: '/bu-dashboard/utilities/requests', label: 'Requests' });
  if (hasParent) menus.push({ to: '/bu-dashboard/utilities/deposits', label: 'Single Deposited Form' });
  return {
    key: 'utilities',
    name: 'Utilities',
    home: '/bu-dashboard/utilities/dashboard',
    menus,
  };
}

const CAMERA_LINKS = {
  calendar: { to: '/attendance/camera/calendar', label: 'Camera Calendar' },
  request: { to: '/attendance/camera/request', label: 'Request Camera' },
  myRequests: { to: '/attendance/camera/my-requests', label: 'My Camera Requests' },
  inbox: { to: '/attendance/camera/inbox', label: 'Media Inbox' },
};

/**
 * Camera Booking menu links for this user's roles. Faculty admins request
 * and track bookings; media users review them, and the inbox shows a badge.
 */
function buildCameraMenus({ faculty, media, pendingCount }) {
  if (!faculty && !media) return [];
  const items = [CAMERA_LINKS.calendar];
  if (faculty || media) items.push(CAMERA_LINKS.request);
  if (faculty) items.push(CAMERA_LINKS.myRequests);
  if (media) {
    items.push({
      ...CAMERA_LINKS.inbox,
      label: (
        <span>
          {CAMERA_LINKS.inbox.label}
          {pendingCount > 0 && (
            <span
              aria-label={`${pendingCount} pending`}
              style={{
                marginLeft: 6,
                padding: '0 6px',
                borderRadius: 999,
                background: '#d97706',
                color: '#fff',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {pendingCount}
            </span>
          )}
        </span>
      ),
    });
  }
  return items;
}

function useClickOutside(ref, onClose) {
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [ref, onClose]);
}

function EmployeeAttendanceLayout({ children }) {
  const { user, logout, isHr, isAdmission, isExam, isPostgraduate, isUtilities, hrApiMissing } = useEmployeeAttendanceAuth();
  const { user: attendanceUser, selectFaculty, getToken } = useAttendanceAuth();
  const isTutor = Boolean(user?.is_tutor);
  const isFacultyAdmin = Boolean(user?.is_faculty_admin);
  const cameraFaculty = isFacultyAdmin || hasCameraFacultyRole(attendanceUser);
  const cameraMedia = hasCameraMediaRole(attendanceUser) || hasCameraMediaRole(user);
  const [cameraPendingCount, setCameraPendingCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const appsRef = useRef(null);
  const userRef = useRef(null);
  const reportsRef = useRef(null);

  useClickOutside(appsRef, () => setAppsOpen(false));
  useClickOutside(userRef, () => setUserOpen(false));
  useClickOutside(reportsRef, () => setReportsOpen(false));

  useEffect(() => {
    setMobileOpen(false);
    setAppsOpen(false);
    setUserOpen(false);
    setReportsOpen(false);
  }, [location.pathname]);

  // Pending-count badge for the media inbox. Refreshes on navigation and
  // whenever an approve or reject changes the queue.
  useEffect(() => {
    if (!cameraMedia) {
      setCameraPendingCount(0);
      return undefined;
    }
    let cancelled = false;
    const refreshPendingCount = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const result = await attendanceAPI.listCameraBookings({ state: 'pending' }, token);
        if (!cancelled) setCameraPendingCount(result.pending_count || 0);
      } catch {
        // The badge is best-effort; the inbox page reports real errors.
      }
    };
    refreshPendingCount();
    window.addEventListener(CAMERA_PENDING_CHANGED_EVENT, refreshPendingCount);
    return () => {
      cancelled = true;
      window.removeEventListener(CAMERA_PENDING_CHANGED_EVENT, refreshPendingCount);
    };
    // getToken is stable for the session; reload the count on navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraMedia, location.pathname]);

  const showStaffNav = STAFF_ATTENDANCE_UI_ENABLED && (
    Boolean(user?.is_staff) || (!isHr && !isAdmission && !isExam && !isPostgraduate)
  );

  // Faculty admins keep their app; camera links are added to its menu.
  const facultyAdminApp = useMemo(
    () => ({
      ...APPS.facultyAdmin,
      menus: [
        ...APPS.facultyAdmin.menus,
        ...buildCameraMenus({ faculty: cameraFaculty, media: cameraMedia, pendingCount: cameraPendingCount }),
      ],
    }),
    [cameraFaculty, cameraMedia, cameraPendingCount]
  );

  // Users who have camera access but are not faculty admins get their own app.
  const cameraApp = useMemo(
    () => ({
      key: 'camera',
      name: 'Camera Booking',
      home: '/attendance/camera/calendar',
      menus: buildCameraMenus({ faculty: cameraFaculty, media: cameraMedia, pendingCount: cameraPendingCount }),
    }),
    [cameraFaculty, cameraMedia, cameraPendingCount]
  );
  const cameraAppVisible = !isFacultyAdmin && (cameraFaculty || cameraMedia);

  const utilitiesApp = useMemo(
    () => buildUtilitiesApp(user?.utilities?.companies || []),
    [user]
  );

  const availableApps = useMemo(() => {
    const list = [];
    if (showStaffNav) list.push(APPS.staff);
    if (isTutor) list.push(APPS.tutor);
    if (isFacultyAdmin) list.push(facultyAdminApp);
    if (cameraAppVisible) list.push(cameraApp);
    if (isHr) list.push(APPS.hr);
    if (isAdmission) list.push(APPS.admission);
    if (isExam) list.push(APPS.exam);
    if (isPostgraduate) list.push(APPS.postgraduate);
    if (isUtilities) list.push(utilitiesApp);
    return list;
  }, [showStaffNav, isHr, isAdmission, isExam, isPostgraduate, isUtilities, utilitiesApp, isTutor, isFacultyAdmin, facultyAdminApp, cameraApp, cameraAppVisible]);

  const currentApp = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/attendance/camera/') && (isFacultyAdmin || cameraAppVisible)) {
      return isFacultyAdmin ? facultyAdminApp : cameraApp;
    }
    if (path.includes('/attendance/faculty-admin/') && isFacultyAdmin) {
      return facultyAdminApp;
    }
    if (path.includes('/attendance/tutor/') && isTutor) return APPS.tutor;
    if (path === '/attendance/dashboard') {
      if (isTutor) return APPS.tutor;
      if (isFacultyAdmin) return facultyAdminApp;
    }
    if (path.includes('/utilities/') && isUtilities) return utilitiesApp;
    if (path.includes('/postgraduate/') && isPostgraduate) return APPS.postgraduate;
    if (path.includes('/exams/') && isExam) return APPS.exam;
    if (path.includes('/admissions/') && isAdmission) return APPS.admission;
    if (path.includes('/hr/') && isHr) return APPS.hr;
    if (showStaffNav) return APPS.staff;
    if (isPostgraduate) return APPS.postgraduate;
    if (isExam) return APPS.exam;
    if (isAdmission) return APPS.admission;
    if (isHr) return APPS.hr;
    if (isUtilities) return utilitiesApp;
    return availableApps[0] || APPS.staff;
  }, [location.pathname, isAdmission, isHr, isExam, isPostgraduate, isUtilities, utilitiesApp, isTutor, isFacultyAdmin, showStaffNav, availableApps, facultyAdminApp, cameraApp, cameraAppVisible]);

  const isOfficerPage =
    location.pathname.includes('/hr/') ||
    location.pathname.includes('/admissions/') ||
    location.pathname.includes('/exams/') ||
    location.pathname.includes('/postgraduate/');

  const managedFaculties = attendanceUser?.faculties || [];
  const selectedFacultyId = attendanceUser?.faculty?.id || attendanceUser?.faculty_id || '';

  const handleFacultyChange = (event) => {
    if (selectFaculty(event.target.value)) {
      // Remount the active page so every request immediately uses the new scope.
      window.location.reload();
    }
  };

  const closeAll = () => {
    setMobileOpen(false);
    setAppsOpen(false);
    setUserOpen(false);
    setReportsOpen(false);
  };

  const renderMenuItem = (item, mobile = false) => {
    if (item.children?.length) {
      const childActive = item.children.some((child) =>
        location.pathname.startsWith(child.to)
      );
      if (mobile) {
        return (
          <div key={item.label} className="ea-o-navbar__submenu-block">
            <p className="ea-o-navbar__mobile-app">{item.label}</p>
            {item.children.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                className={({ isActive }) =>
                  `ea-o-navbar__menu${isActive ? ' is-active' : ''}`
                }
                onClick={closeAll}
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        );
      }
      return (
        <div
          key={item.label}
          className={`ea-o-navbar__menu-wrap${reportsOpen || childActive ? ' is-open' : ''}`}
          ref={reportsRef}
        >
          <button
            type="button"
            className={`ea-o-navbar__menu ea-o-navbar__menu--dropdown${
              childActive ? ' is-active' : ''
            }${reportsOpen ? ' is-open' : ''}`}
            aria-haspopup="menu"
            aria-expanded={reportsOpen}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setReportsOpen((o) => !o);
              setAppsOpen(false);
              setUserOpen(false);
            }}
          >
            {item.label}
            <MdExpandMore size={16} aria-hidden />
          </button>
          {reportsOpen ? (
            <div className="ea-o-dropdown ea-o-dropdown--reports" role="menu">
              {item.children.map((child) => (
                <NavLink
                  key={child.to}
                  to={child.to}
                  role="menuitem"
                  className={({ isActive }) =>
                    `ea-o-dropdown__item${isActive ? ' is-active' : ''}`
                  }
                  onClick={closeAll}
                >
                  {child.label}
                </NavLink>
              ))}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        className={({ isActive }) => `ea-o-navbar__menu${isActive ? ' is-active' : ''}`}
        onClick={mobile ? closeAll : undefined}
      >
        {item.label}
      </NavLink>
    );
  };

  return (
    <div className="ea-app ea-dr ea-dr--topnav">
      <header className="ea-o-navbar" role="banner">
        <button
          type="button"
          className="ea-o-navbar__burger"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
        </button>

        <a href="/" className="ea-o-navbar__logo" title="Benadir University">
          <img src={BU_LOGO} alt="Benadir University" />
        </a>

        <div className="ea-o-navbar__apps" ref={appsRef}>
          <button
            type="button"
            className={`ea-o-navbar__apps-btn${appsOpen ? ' is-open' : ''}`}
            onClick={() => {
              setAppsOpen((o) => !o);
              setUserOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={appsOpen}
            title="Apps"
          >
            <MdApps size={20} aria-hidden />
          </button>
          {appsOpen ? (
            <div className="ea-o-dropdown ea-o-dropdown--apps" role="menu">
              {availableApps.map((app) => (
                <button
                  key={app.key}
                  type="button"
                  role="menuitem"
                  className={`ea-o-dropdown__item${
                    currentApp.key === app.key ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    navigate(app.home);
                    closeAll();
                  }}
                >
                  {app.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="ea-o-navbar__app-name"
          onClick={() => {
            setAppsOpen((o) => !o);
            setUserOpen(false);
          }}
        >
          {currentApp.name}
          <MdExpandMore size={18} aria-hidden />
        </button>

        <nav className="ea-o-navbar__menus ea-o-navbar__menus--desktop" aria-label="Main">
          {currentApp.menus.map((item) => renderMenuItem(item, false))}
        </nav>


        <div className="ea-o-navbar__systray" ref={userRef}>
          <button
            type="button"
            className={`ea-o-navbar__user-btn${userOpen ? ' is-open' : ''}`}
            onClick={() => {
              setUserOpen((o) => !o);
              setAppsOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={userOpen}
          >
            <span className="ea-o-navbar__avatar" aria-hidden>
              {user?.image_url ? (
                <img
                  src={user.image_url}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.hidden = false;
                  }}
                />
              ) : null}
              <span
                className="ea-o-navbar__avatar-initials"
                hidden={Boolean(user?.image_url)}
              >
                {getInitials(user?.name)}
              </span>
            </span>
            <span className="ea-o-navbar__user-name">{user?.name || 'User'}</span>
            <MdExpandMore size={16} aria-hidden />
          </button>
          {userOpen ? (
            <div className="ea-o-dropdown ea-o-dropdown--user" role="menu">
              <div className="ea-o-dropdown__header">{user?.name}</div>
              <NavLink
                to="/bu-dashboard/profile"
                role="menuitem"
                className="ea-o-dropdown__item"
                onClick={closeAll}
              >
                <MdPerson size={16} aria-hidden />
                Profile
              </NavLink>
              <NavLink
                to="/bu-dashboard/change-password"
                role="menuitem"
                className="ea-o-dropdown__item"
                onClick={closeAll}
              >
                <MdLock size={16} aria-hidden />
                Change Password
              </NavLink>
              <button
                type="button"
                role="menuitem"
                className="ea-o-dropdown__item ea-o-dropdown__item--danger"
                onClick={() => {
                  closeAll();
                  logout();
                }}
              >
                <MdLogout size={16} aria-hidden />
                Log out
              </button>
            </div>
          ) : null}
        </div>
      </header>

      {mobileOpen ? (
        <>
          <div className="ea-o-navbar-overlay" onClick={closeAll} aria-hidden />
          <nav className="ea-o-navbar__menus ea-o-navbar__menus--mobile" aria-label="Mobile">
            <p className="ea-o-navbar__mobile-app">{currentApp.name}</p>
            {currentApp.menus.map((item) => renderMenuItem(item, true))}
            {availableApps.length > 1 ? (
              <>
                <p className="ea-o-navbar__mobile-app">Switch app</p>
                {availableApps
                  .filter((a) => a.key !== currentApp.key)
                  .map((app) => (
                    <button
                      key={app.key}
                      type="button"
                      className="ea-o-navbar__menu ea-o-navbar__menu--btn"
                      onClick={() => {
                        navigate(app.home);
                        closeAll();
                      }}
                    >
                      {app.name}
                    </button>
                  ))}
              </>
            ) : null}
          </nav>
        </>
      ) : null}

      <div className="ea-main-wrap">
        <main className={`ea-main${isOfficerPage ? ' ea-main--hr' : ''}`}>
          {hrApiMissing && (
            <div className="ea-hr-api-banner" role="status">
              HR dashboard is visible because you are an HR user
              {user?.department ? ` (${user.department})` : ''}, but report data cannot load
              until the server exposes <code>/api/employee-attendance/hr/*</code>. Ask your
              administrator to upgrade <strong>openeducat_attendance</strong> and restart Odoo.
            </div>
          )}
          {currentApp.key === 'facultyAdmin' && managedFaculties.length > 1 ? (
            <section className="ea-faculty-scope" aria-label="Faculty filter">
              <div className="ea-faculty-scope__copy">
                <strong>Faculty</strong>
                <span>Select the faculty whose information you want to manage.</span>
              </div>
              <select
                value={selectedFacultyId}
                onChange={handleFacultyChange}
                aria-label="Select faculty to manage"
              >
                {managedFaculties.map((faculty) => (
                  <option key={faculty.id} value={faculty.id}>
                    {faculty.name}
                  </option>
                ))}
              </select>
            </section>
          ) : null}

          {children}
        </main>
      </div>
    </div>
  );
}

export default EmployeeAttendanceLayout;
